import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Share2, Copy, Check as CheckIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import ArtworkModal from '@/components/ArtworkModal';
import ProgressiveImage from '@/components/ProgressiveImage';
import WishlistButton from '@/components/WishlistButton';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/RegionContext';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CollectionDetail = () => {
  const { collectionId } = useParams();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [copied, setCopied] = useState(false);
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/collections/${collectionId}`)
      .then(res => {
        setCollection(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [collectionId]);

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/collections/${collectionId}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: collection?.name || 'Art Collection',
          text: collection?.description || `Browse this collection by Gangadhar Som`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 sm:pt-32 pb-16 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-screen-xl mx-auto">
          <div className="h-8 w-48 bg-muted animate-pulse rounded mb-4" />
          <div className="h-4 w-96 bg-muted animate-pulse rounded mb-10" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <div key={i} className="aspect-[3/4] bg-muted animate-pulse rounded-lg" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="min-h-screen pt-24 sm:pt-32 pb-16 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-muted-foreground">Collection not found</p>
          <Link to="/collections" className="text-accent mt-2 inline-block hover:underline">
            Back to Collections
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${collection.name} - Gangadhar Som Art Collection`}
        description={collection.description || `Browse the "${collection.name}" collection of paintings by Gangadhar Som.`}
        url={`${window.location.origin}/collections/${collection.id}`}
        image={`${process.env.REACT_APP_BACKEND_URL}/api/collections/${collection.id}/og-image`}
      />
      <div className="min-h-screen pt-24 sm:pt-32 pb-16 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="collection-detail-page">
        <div className="max-w-screen-xl mx-auto">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10 sm:mb-14">
            <div className="flex items-center justify-between mb-4">
              <Link to="/collections" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-collections">
                <ArrowLeft size={16} /> All Collections
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                className="gap-1.5"
                data-testid="share-collection-btn"
              >
                {copied ? <CheckIcon size={14} /> : <Share2 size={14} />}
                {copied ? 'Copied!' : 'Share'}
              </Button>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light mb-2" data-testid="collection-title">
              {collection.name}
            </h1>
            {collection.description && (
              <p className="text-muted-foreground text-sm sm:text-base max-w-2xl" data-testid="collection-description">
                {collection.description}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              {collection.artworks?.length || 0} artwork{(collection.artworks?.length || 0) !== 1 ? 's' : ''}
            </p>
          </motion.div>

          {/* Artwork grid */}
          {collection.artworks && collection.artworks.length > 0 ? (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-6">
              {collection.artworks.map((artwork, index) => (
                <motion.div
                  key={artwork.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  className="break-inside-avoid mb-4 sm:mb-6"
                >
                  <div
                    className="group relative bg-card rounded-lg overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                    onClick={() => setSelectedArtwork(artwork)}
                    data-testid={`collection-artwork-${artwork.id}`}
                  >
                    <div className="relative protected-image select-none">
                      <ProgressiveImage
                        src={artwork.thumb_url || artwork.image_url}
                        placeholder={artwork.placeholder}
                        alt={artwork.title}
                        className="w-full h-auto pointer-events-none select-none"
                        style={{ WebkitUserDrag: 'none', userSelect: 'none' }}
                      />
                      <div className="watermark-overlay" />
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <WishlistButton artworkId={artwork.id} />
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-serif text-sm sm:text-base font-light truncate">{artwork.title}</h3>
                      {artwork.price && (
                        <p className="text-xs text-muted-foreground mt-0.5">{formatPrice(artwork.price)}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-12">No artworks in this collection yet.</p>
          )}
        </div>
      </div>

      <ArtworkModal
        artwork={selectedArtwork}
        isOpen={!!selectedArtwork}
        onClose={() => setSelectedArtwork(null)}
      />
    </>
  );
};

export default CollectionDetail;
